/* eslint-disable react-hooks/exhaustive-deps */
import { Table, Button, Space, Switch, Tag, Tooltip, Typography } from "antd";
import axios from "axios";
import dayjs from "dayjs";
import { useEffect, useState } from "react";

import AllowlistManagementModal from "./AllowlistManagementModal";
import CreateEditDiscountModal from "./CreateEditDiscountModal";
import { DISCOUNT_TYPES, hasDiscountEnded, resolveDiscountAudience } from "./helper";
import {
  showErrorNotification,
  showSuccessNotification
} from "../../../utils/notification";
import { clamp } from "../../../utils/responsiveHelpers";

const { Text } = Typography;

const DiscountManagementDash = ({ adminConfig, ROOT_URL }) => {
  const BASE_URL = "admin/payments/discounts";

  const [discountsList, setDiscountsList] = useState([]);
  const [showActiveDiscountsOnly, setShowActiveDiscountsOnly] = useState(true);

  const [isCreateEditDiscountModalOpen, setIsCreateEditDiscountModalOpen] = useState(false);
  const [isAllowListManagementModalOpen, setIsAllowListManagementModalOpen] = useState(false);
  const [initialValues, setInitialValues] = useState({});

  const [targetBatchOptions, setTargetBatchOptions] = useState([]);
  const [targetCourseOptions, setTargetCourseOptions] = useState([]);

  const handleExpiredDisplayedBanners = (discounts) => {
    const activeDisplayedBannerExpired = discounts.find(
      (discount) => discount?.isBannerDisplayed && hasDiscountEnded(discount)
    );

    activeDisplayedBannerExpired && handleBannerToggle(activeDisplayedBannerExpired.key, false);
  };

  const fetchDiscountList = () => {
    axios
      .get(`${ROOT_URL}/${BASE_URL}/list`, {
        params: {
          active: showActiveDiscountsOnly
        }
      })
      .then((res) => {
        const { discounts } = res.data;
        // Handle any expired active banners
        handleExpiredDisplayedBanners(discounts);

        setDiscountsList(discounts);
        showSuccessNotification("Discounts fetched successfully!");
      })
      .catch((err) => {
        showErrorNotification(err);
      });
  };

  const fetchDiscountCreationFormPrerequisites = () => {
    axios
      .get(`${ROOT_URL}/${BASE_URL}/form-prerequisites`)
      .then((res) => {
        const { targetBatchOptions, targetCourseOptions } = res.data;
        setTargetBatchOptions(targetBatchOptions);
        setTargetCourseOptions(targetCourseOptions);
      })
      .catch((err) => {
        showErrorNotification(err);
      });
  };

  const handleCreateEditDiscount = (discountDetails) => {
    if (initialValues?.key !== undefined) {
      axios
        .post(`${ROOT_URL}/${BASE_URL}/update`, {
          discountParams: discountDetails,
          discountId: initialValues.key
        })
        .then(() => {
          setIsCreateEditDiscountModalOpen(false);
          showSuccessNotification("Discount Updated Successfully");
          fetchDiscountList();
        })
        .catch((err) => {
          showErrorNotification(err);
        });
    } else {
      axios
        .post(`${ROOT_URL}/${BASE_URL}/create`, {
          discountParams: discountDetails
        })
        .then(() => {
          setIsCreateEditDiscountModalOpen(false);
          showSuccessNotification("Discount Created Successfully");
          fetchDiscountList();
        })
        .catch((err) => {
          showErrorNotification(err);
        });
    }
  };

  // Many discounts can show a banner. Each user sees the banner of their best discount.
  const handleBannerToggle = (discountId, newValue) => {
    axios
      .post(`${ROOT_URL}/${BASE_URL}/toggle-banner-display`, {
        discountId,
        isBannerDisplayed: newValue
      })
      .then(() => {
        showSuccessNotification("Banner display updated successfully!");
        fetchDiscountList();
      })
      .catch((err) => {
        showErrorNotification(err);
      });
  };

  const isBannerToggleDisabled = (record) => hasDiscountEnded(record);

  const columns = [
    {
      title: "S. No",
      dataIndex: "index",
      key: "index"
    },
    {
      title: "Discount Details",
      key: "details",
      render: (record) => (
        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <Text strong>{record.name}</Text>
          <Text
            copyable={{ text: record.displayCouponCode }}
            style={{ fontSize: "12px", color: "#666" }}
          >
            🎟️ {record.displayCouponCode}
          </Text>
          <div>
            <Tag color={record.discountType === DISCOUNT_TYPES.FIXED_PRICE ? "magenta" : "cyan"}>
              {record.discountType === DISCOUNT_TYPES.FIXED_PRICE ? "Fixed Price" : "Percentage"}
            </Tag>
          </div>
        </div>
      )
    },
    {
      title: "Value / Pricing",
      key: "pricing",
      render: (record) => {
        if (record.discountType === DISCOUNT_TYPES.FIXED_PRICE) {
          return (
            <div style={{ display: "flex", flexDirection: "column" }}>
              <Text strong>₹{record.fixedDiscountAmountINR || record.effectiveDiscount}</Text>
            </div>
          );
        }
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
            <Text strong>Effective: {Number(record.effectiveDiscount * 100).toFixed(2)}%</Text>
            <Text type="secondary" style={{ fontSize: "11px" }}>
              Quoted: {record.quotedPriceFactor} | Actual: {record.actualPriceFactor}
            </Text>
          </div>
        );
      }
    },
    {
      title: "Validity & Usage",
      key: "validity",
      render: (record) => (
        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <Text style={{ fontSize: "12px" }}>
            🗓️{" "}
            {record.validityStartTime ? dayjs(record.validityStartTime).format("DD MMM YYYY | HH:mm") : "-"}{" "}
            to{" "}
            {record.validityEndTime
              ? dayjs(record.validityEndTime).format("DD MMM YYYY | HH:mm")
              : "No Expiry"}
          </Text>
          <Text style={{ fontSize: "12px" }}>
            🔄 Usage: {record.usageCount} / {record.usageLimit || "∞"}
          </Text>
        </div>
      )
    },
    {
      title: "Audience & Targeting",
      key: "audience",
      width: 280,
      render: (record) => {
        const hasEmailDomains = record.targetEmailDomains?.length > 0;
        const hasBatches = record.targetBatchIds?.length > 0;
        const hasCourses = record.targetCourseIds?.length > 0;
        const audience = resolveDiscountAudience(record);

        return (
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
              {!record.isPublic ? <Tag color="red">Private</Tag> : <Tag color="green">Public</Tag>}
              <Tooltip
                title={
                  audience.reachesNobody
                    ? "A private discount needs a targeting rule or allowlisted emails."
                    : undefined
                }
              >
                <Tag color={audience.color}>{audience.label}</Tag>
              </Tooltip>
            </div>

            {(hasEmailDomains || hasBatches || hasCourses) && (
              <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                {hasEmailDomains && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                    {record.targetEmailDomains.map((domain) => (
                      <Tag color="blue" key={domain}>
                        @{domain}
                      </Tag>
                    ))}
                  </div>
                )}
                {hasBatches && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                    {record.targetBatchIds.map((id) => {
                      const label =
                        targetBatchOptions.find((opt) => opt.value === id)?.label ||
                        "Unknown Batch";
                      return (
                        <Tag color="purple" key={id}>
                          🎓 {label}
                        </Tag>
                      );
                    })}
                  </div>
                )}
                {hasCourses && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                    {record.targetCourseIds.map((id) => {
                      const label =
                        targetCourseOptions.find((opt) => opt.value === id)?.label ||
                        "Unknown Course";
                      return (
                        <Tag color="orange" key={id}>
                          📚 {label}
                        </Tag>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      }
    },
    {
      title: "Created By",
      dataIndex: "createdBy",
      key: "createdBy"
    },
    {
      title: "Banner Display",
      dataIndex: "isBannerDisplayed",
      key: "isBannerDisplayed",
      render: (isBannerDisplayed, record) => (
        <Switch
          disabled={isBannerToggleDisabled(record)}
          checked={isBannerDisplayed}
          onChange={(checked) => handleBannerToggle(record.key, checked)}
        />
      )
    },
    {
      title: "Actions",
      key: "action",
      render: (row) => (
        <div
          id="payments_discount_row_actions"
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-around"
          }}
        >
          <Space wrap>
            <>
              {adminConfig?.hasDiscountsManagementAccess && (
                <>
                  <Button
                    type="primary"
                    onClick={() => {
                      setInitialValues(row);
                      setIsCreateEditDiscountModalOpen(true);
                    }}
                  >
                    Edit
                  </Button>
                </>
              )}
            </>
            <>
              {adminConfig?.hasDiscountsManagementAccess && (
                <>
                  <Button
                    type="primary"
                    onClick={() => {
                      setInitialValues(row);
                      setIsAllowListManagementModalOpen(true);
                    }}
                  >
                    Manage Allowlist
                  </Button>
                </>
              )}
            </>
          </Space>
        </div>
      )
    }
  ];

  useEffect(() => {
    fetchDiscountList();
    fetchDiscountCreationFormPrerequisites();
  }, [showActiveDiscountsOnly]);

  return (
    <div
      style={{
        marginTop: "20px",
        marginLeft: clamp(12, 30),
        marginRight: clamp(12, 30)
      }}
    >
      <div
        style={{
          marginBottom: "10px",
          display: "flex",
          flexDirection: "row",
          flexWrap: "wrap",
          gap: "10px"
        }}
      >
        {adminConfig?.hasDiscountsManagementAccess && (
          <Button
            type="primary"
            onClick={() => {
              setInitialValues({});
              setIsCreateEditDiscountModalOpen(true);
            }}
          >
            Create Discount
          </Button>
        )}
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <Switch
            checked={showActiveDiscountsOnly}
            onChange={(checked) => setShowActiveDiscountsOnly(checked)}
          />
          <span style={{ fontWeight: 500 }}>Show Active Discounts Only</span>
        </div>
      </div>

      <Table
        columns={columns}
        dataSource={discountsList}
        bordered
        pagination={false}
        scroll={{ x: "max-content" }}
      />
      {isCreateEditDiscountModalOpen === true && (
        <CreateEditDiscountModal
          isModalOpen={isCreateEditDiscountModalOpen}
          setIsModalOpen={setIsCreateEditDiscountModalOpen}
          isEdit={initialValues?.key !== undefined}
          initialValues={initialValues || {}}
          handleCreateEditDiscount={handleCreateEditDiscount}
          targetBatchOptions={targetBatchOptions}
          targetCourseOptions={targetCourseOptions}
        />
      )}
      {isAllowListManagementModalOpen === true && (
        <AllowlistManagementModal
          isModalOpen={isAllowListManagementModalOpen}
          setIsModalOpen={setIsAllowListManagementModalOpen}
          discountParams={{ discountId: initialValues?.key, discountName: initialValues?.name }}
          BASE_URL={BASE_URL}
          ROOT_URL={ROOT_URL}
        />
      )}
    </div>
  );
};

export default DiscountManagementDash;
